import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Toast as ToastType } from '../../contexts/ToastContext';
import './Toast.css';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

const ICONS: Record<string, string> = {
  success: 'bi-check-circle-fill',
  error: 'bi-exclamation-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
  info: 'bi-info-circle-fill',
};

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(toast.duration);
  const startTimeRef = useRef(Date.now());

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    if (isPaused) return;

    startTimeRef.current = Date.now();
    timeoutRef.current = setTimeout(handleDismiss, remainingRef.current);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isPaused, handleDismiss]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      remainingRef.current -= Date.now() - startTimeRef.current;
    }
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  return (
    <div
      className={`toast toast--${toast.type} ${isExiting ? 'toast--exiting' : ''}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <i className={`bi ${ICONS[toast.type]} toast__icon`}></i>
      <span className="toast__message">{toast.message}</span>
      <button
        className="toast__close"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        <i className="bi bi-x"></i>
      </button>
    </div>
  );
};

export default Toast;
